<template>
  <div :style="containerStyle">
    <Suspense v-if="isVisible" @resolve="onResolved">
      <slot />
      <template #fallback>
        <div
          class="shimmer w-full rounded-2xl"
          :style="{ minHeight: props.minHeight }"
        />
      </template>
    </Suspense>
    <template v-else>
      <div class="shimmer w-full flex-1 rounded-2xl" />
      <div ref="sentinelRef" aria-hidden="true" />
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

  const props = withDefaults(
    defineProps<{
      minHeight?: string;
      root?: HTMLElement | null;
    }>(),
    {
      minHeight: '0px',
      root: null,
    },
  );

  const sentinelRef = ref<HTMLElement | null>(null);
  const isVisible = ref(false);
  const hasResolved = ref(false);
  let observer: IntersectionObserver | null = null;

  const onResolved = () => {
    hasResolved.value = true;
  };

  const containerStyle = computed(() => ({
    ...(hasResolved.value ? {} : { minHeight: props.minHeight }),
    ...(isVisible.value
      ? {}
      : { display: 'flex', flexDirection: 'column' as const }),
  }));

  const setupObserver = () => {
    observer?.disconnect();
    observer = null;
    if (!sentinelRef.value || isVisible.value) {
      return;
    }
    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          isVisible.value = true;
          observer?.disconnect();
          observer = null;
        }
      },
      { root: props.root, rootMargin: '0px' },
    );
    observer.observe(sentinelRef.value);
  };

  onMounted(setupObserver);
  watch(() => props.root, setupObserver);

  onUnmounted(() => {
    observer?.disconnect();
    observer = null;
  });
</script>
